import {
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "../ui/Sonner";
import LogoSmall from "@/res/logos/solarcloud/logo-icon.svg";
import {
  Mail,
  GalleryVerticalEnd,
  Map,
  ShoppingCart,
  Sun,
  Hourglass,
  Activity,
  ScrollText,
  ReceiptText,
  Vault,
  Banknote,
  MapPin,
  Zap,
  Flame,
  Link,
} from "lucide-react";
import { useFullDashboardPath } from "@/hooks/useFullDashboardPath";
import Footer from "../ui/Footer";
import ScrollToTop from "../ScrollToTop";
import { useOutstandingChecks } from "@/hooks/useOutstandingChecks";
import { useMemo, useEffect, useRef } from "react";
import { useLinkCounts } from "@/hooks/useLinkCounts";
import { useLocation } from "react-router";
import { useTrackerSummary } from "@/queries/tracker/useTrackerSummary";

export type MenuLinkItem = {
  name: string;
  url: string;
  icon: React.ElementType;
};

export type LinkItem = {
  name: string;
  url: string;
  icon: React.ElementType;
  menu?: MenuLinkItem[];
  badge?: string;
  badgeVariant?: "outline" | "solid";
};

type LinkSections = {
  dashboard: LinkItem[];
  links: LinkItem[];
  panels: LinkItem[];
  documents: LinkItem[];
};

let data: LinkSections = {
  dashboard: [
    {
      name: "Dashboard",
      url: "",
      icon: Sun,
    },
    {
      name: "Tracker",
      url: "tracker",
      icon: Activity,
    },
    {
      name: "Location",
      url: "locations",
      icon: MapPin,
    },
  ],
  links: [
    {
      name: "Electricity Account",
      url: "allocations/electricity",
      icon: Zap,
      // badge: "1",
    },
    {
      name: "Gas Account",
      url: "allocations/gas",
      icon: Flame,
    },
    {
      name: "Bank Details",
      url: "allocations/bank",
      icon: Banknote,
    },
  ],
  panels: [
    // {
    //   name: "Map",
    //   url: "map",
    //   icon: Map,
    // },
  ],
  documents: [
    {
      name: "Paperwork",
      url: "orders",
      icon: ReceiptText,
    },
    {
      name: "Terms & Conditions",
      url: "terms",
      icon: ScrollText,
      // badge: "!",
    },

    // {
    //   name: "Contact Us",
    //   url: "contact",
    //   icon: Mail,
    // },
  ],
};

export type SidebarDataType = typeof data;

type DashboardLayoutProps = {
  children: React.ReactNode;
};

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const fullDashboardPath = useFullDashboardPath();
  const { pathname } = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    unacceptedTermsCount,
    unallocatedElectricityPanels,
    powerGenerationInfo,
  } = useOutstandingChecks();
  const { gasLinked, electricityLinked, bankLinked, unlinked, pendingCapacity, total } =
    useLinkCounts();
  const { data: trackerSummary } = useTrackerSummary();

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Create data with dynamic badges for terms and conditions and allocations
  const dataWithBadge = useMemo(() => {
    const updatedData = { ...data };

    const trackerPanels = trackerSummary
      ? Object.values(trackerSummary).reduce((sum, stage) => sum + (stage?.panelCount || 0), 0)
      : 0;

    if (trackerPanels > 0) {
      updatedData.dashboard = [
        data.dashboard[0],
        {
          name: "Order Tracker",
          url: "order-tracker",
          icon: Hourglass,
        },
        ...data.dashboard.slice(1),
      ];
    } else {
      updatedData.dashboard = [...data.dashboard];
    }

    // Add badge to Terms & Conditions
    updatedData.documents = data.documents.map((item) => {
      if (item.url === "terms") {
        return {
          ...item,
          badge:
            unacceptedTermsCount > 0
              ? unacceptedTermsCount.toString()
              : undefined,
        };
      }
      return item;
    });

    // Add badge to Electricity Account

    updatedData.links = data.links.map((item) => {
      if (item.url === "allocations/electricity") {
        return {
          ...item,
          badge:
            electricityLinked > 0 ? electricityLinked.toString() : undefined,
          badgeVariant: "outline",
        };
      }
      if (item.url === "allocations/gas") {
        return {
          ...item,
          badge: gasLinked > 0 ? gasLinked.toString() : undefined,
          badgeVariant: "outline",
        };
      }
      if (item.url === "allocations/bank") {
        return {
          ...item,
          badge: bankLinked > 0 ? bankLinked.toString() : undefined,
          badgeVariant: "outline",
        };
      }
      return item;
    });

    if (unlinked > 0) {
      updatedData.links.push({
        name: "Unlinked",
        url: "allocations",
        icon: Link,
        menu: [
          {
            name: "Electricity",
            url: "allocations/electricity",
            // url: "allocations/electricity?createLink=true",
            icon: Zap,
          },
          {
            name: "Gas",
            url: "allocations/gas",
            // url: "allocations/gas?createLink=true",
            icon: Flame,
          },
        ],
        badge: unlinked > 0 ? unlinked.toString() : undefined,
      });
    }

    return updatedData;
  }, [
    unacceptedTermsCount,
    unallocatedElectricityPanels,
    gasLinked,
    electricityLinked,
    unlinked,
    pendingCapacity,
    trackerSummary,
  ]);

  const currentNavItem = Object.values(dataWithBadge)
    .flat()
    .find((item) => item.url === fullDashboardPath);

  return (
    <SidebarProvider>
      <AppSidebar data={dataWithBadge} />
      <div
        ref={scrollContainerRef}
        className="bg-brand-neutral-800 h-screen overflow-y-auto flex-1 min-w-0"
      >
        <HeaderNav title={currentNavItem?.name ?? "Dashboard"} />
        <main className="p-4 min-h-[calc(100vh-200px)] mt-16 sm:mt-0">
          {children}
        </main>
        <Footer />
      </div>
      <Toaster />
    </SidebarProvider>
  );
}

function HeaderNav({ title }: { title: string }) {
  const { isMobile, toggleSidebar } = useSidebar();

  return (
    <>
      {isMobile && (
        <header className="fixed top-0 left-0 right-0 z-50  h-16 shrink-0 flex items-center gap-2 bg-brand-neutral-900">
          <div className="flex items-center justify-between gap-2 px-4 w-full">
            <div className="flex items-center gap-2">
              <button onClick={toggleSidebar} className="size-8">
                <LogoSmall />
              </button>
              <Separator
                orientation="vertical"
                className="mr-2 data-[orientation=vertical]:h-4"
              />
              <h1>{title}</h1>
            </div>
            <SidebarTrigger className="-ml-1" />
          </div>
        </header>
      )}
    </>
  );
}
